import React, { useState, useEffect } from "react";

/**
 * Function to get user's IP address
 * @returns {Promise<string>} The user's IP address or empty string on error
 */
const getIpAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Error fetching IP address:", error);
    return "";
  }
};

/**
 * getUserLocationInfo - Function that collects and returns device and location info
 * @returns {Promise<Object>} Object containing device info and location data
 */
const getUserLocationInfo = async () => {
  // Initialize the return object with the structure you specified
  const userInfo = {
    device: "unknown",
    ipAddressLocation: "",
    lat: 0,
    lng: 0,
    location: "",
    userAgent: "",
  };

  try {
    // Get device information - detect device type (desktop, mobile, tablet, etc.)
    const getDeviceInfo = () => {
      const userAgent = navigator.userAgent || "unknown";

      // Detect device type
      let deviceType = "Desktop"; // Default to desktop

      // Check for mobile devices
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;

      if (mobileRegex.test(userAgent)) {
        // Further distinguish between tablet and phone
        if (
          /iPad|tablet|Tablet/i.test(userAgent) ||
          (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
        ) {
          deviceType = "Tablet";
        } else {
          deviceType = "Mobile Phone";
        }
      }

      // Check for TV
      if (/Smart-TV|SMART-TV|smarttv|Roku|BRAVIA|CrKey/i.test(userAgent)) {
        deviceType = "Smart TV";
      }

      // Check for game console
      if (/Xbox|PlayStation|Nintendo/i.test(userAgent)) {
        deviceType = "Game Console";
      }

      // Extract OS information
      let osName = "Unknown OS";

      if (userAgent.indexOf("Windows") !== -1) {
        osName = "Windows";
      } else if (userAgent.indexOf("Mac") !== -1) {
        osName = "macOS";
      } else if (userAgent.indexOf("Linux") !== -1) {
        osName = "Linux";
      } else if (userAgent.indexOf("Android") !== -1) {
        osName = "Android";
      } else if (
        userAgent.indexOf("iOS") !== -1 ||
        userAgent.indexOf("iPhone") !== -1 ||
        userAgent.indexOf("iPad") !== -1
      ) {
        osName = "iOS";
      }

      // Extract browser information (optional, keeping for completeness)
      let browserName = "Unknown Browser";

      if (
        userAgent.indexOf("Chrome") !== -1 &&
        userAgent.indexOf("Edg") === -1 &&
        userAgent.indexOf("OPR") === -1
      ) {
        browserName = "Chrome";
      } else if (userAgent.indexOf("Firefox") !== -1) {
        browserName = "Firefox";
      } else if (
        userAgent.indexOf("Safari") !== -1 &&
        userAgent.indexOf("Chrome") === -1
      ) {
        browserName = "Safari";
      } else if (userAgent.indexOf("Edg") !== -1) {
        browserName = "Edge";
      } else if (
        userAgent.indexOf("OPR") !== -1 ||
        userAgent.indexOf("Opera") !== -1
      ) {
        browserName = "Opera";
      } else if (
        userAgent.indexOf("MSIE") !== -1 ||
        userAgent.indexOf("Trident") !== -1
      ) {
        browserName = "Internet Explorer";
      }

      // Additional device capabilities detection
      const hasTouch =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0;

      // Screen size based detection as fallback
      if (deviceType === "Desktop" && hasTouch && window.innerWidth < 1024) {
        deviceType = window.innerWidth < 768 ? "Mobile Phone" : "Tablet";
      }

      return {
        type: deviceType,
        os: osName,
        browser: browserName,
        hasTouch: hasTouch,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        fullUserAgent: userAgent,
      };
    };

    const deviceInfo = getDeviceInfo();
    userInfo.device = deviceInfo.type;
    userInfo.userAgent = deviceInfo.fullUserAgent; // Add userAgent to top level

    // Add more detailed device info if needed
    userInfo.deviceDetails = {
      os: deviceInfo.os,
      browser: deviceInfo.browser,
      screenSize: `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`,
      touchCapable: deviceInfo.hasTouch,
    };

    // Get location via geolocation API (coordinates)
    const getGeoPosition = () => {
      return new Promise((resolve, reject) => {
        if ("geolocation" in navigator) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            },
            (error) => {
              console.warn("Geolocation error:", error);
              reject(error);
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
          );
        } else {
          reject(new Error("Geolocation not available"));
        }
      });
    };

    // Get IP based location using the provided getIpAddress function
    const getIpBasedLocation = async () => {
      try {
        // Get IP address using the dedicated function
        const ipAddress = await getIpAddress();
        userInfo.ipAddressLocation = ipAddress;

        if (ipAddress) {
          // Use the IP to get location data via ipapi.co (free tier with no token required)
          const locationResponse = await fetch(
            `https://ipapi.co/${ipAddress}/json/`
          );
          const locationData = await locationResponse.json();

          // Format location string
          const location = locationData.city
            ? `${locationData.city}${
                locationData.region ? ", " + locationData.region : ""
              }${
                locationData.country_name
                  ? ", " + locationData.country_name
                  : ""
              }`
            : "Unknown location";

          // Get coordinates if available
          const lat = locationData.latitude || 0;
          const lng = locationData.longitude || 0;

          return {
            ipAddressLocation: ipAddress,
            location: location,
            lat: lat,
            lng: lng,
          };
        } else {
          return {
            ipAddressLocation: "",
            location: "Unknown location",
            lat: 0,
            lng: 0,
          };
        }
      } catch (error) {
        console.warn("IP location fetch error:", error);

        // At least try to get the IP address if location lookup fails
        try {
          const ipAddress = await getIpAddress();
          return {
            ipAddressLocation: ipAddress || "",
            location: "Unknown location",
            lat: 0,
            lng: 0,
          };
        } catch (fallbackError) {
          console.error("IP fetch failed:", fallbackError);
          return {
            ipAddressLocation: "",
            location: "Unknown location",
            lat: 0,
            lng: 0,
          };
        }
      }
    };

    // Try to get accurate location from browser geolocation
    try {
      const geoPosition = await getGeoPosition();
      userInfo.lat = geoPosition.lat;
      userInfo.lng = geoPosition.lng;
    } catch (geoError) {
      console.log(
        "Browser geolocation unavailable, using IP-based location only"
      );
    }

    // Get IP and location data
    const ipLocationData = await getIpBasedLocation();
    userInfo.ipAddressLocation = ipLocationData.ipAddressLocation;

    // If we didn't get coordinates from geolocation, use the IP-based ones
    if (userInfo.lat === 0 && userInfo.lng === 0) {
      userInfo.lat = ipLocationData.lat;
      userInfo.lng = ipLocationData.lng;
    }

    // Set the location string
    userInfo.location = ipLocationData.location;

    // If we have coordinates but no location string, try reverse geocoding
    if (
      userInfo.lat !== 0 &&
      userInfo.lng !== 0 &&
      userInfo.location === "Unknown location"
    ) {
      try {
        const geocodeResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${userInfo.lat}&lon=${userInfo.lng}&zoom=18&addressdetails=1`
        );
        const geocodeData = await geocodeResponse.json();

        if (geocodeData.display_name) {
          userInfo.location = geocodeData.display_name;
        }
      } catch (geocodeError) {
        console.warn("Reverse geocoding failed:", geocodeError);
      }
    }
  } catch (error) {
    console.error("Error collecting user info:", error);
  }

  return userInfo;
};

export { getUserLocationInfo, getIpAddress };
